/**
 * Parsers for earn vault info (GET `/earn/vaults`, GET `/earn/vaults/:address`).
 * One file per earn route family — see
 * {@link ./earn-positions-parser}, {@link ./earn-queue-parser}, {@link ./earn-events-parser}.
 */

import { EarnVaultSnapshotApi, EarnVaultWithLatestSnapshotApi } from "../api/earn";
import { EarnVaultRecord, EarnVaultSnapshot } from "../domain/earn";
import { parseParsedEarnAccountFromApi } from "./earn-strategy-parser";

export function parseEarnVaultSnapshotFromApi(api: EarnVaultSnapshotApi | null | undefined): EarnVaultSnapshot | null {
    if (api == null) return null;

    const timestamp = api.timestamp ?? (api.snapshotTs ? Math.floor(new Date(api.snapshotTs).getTime() / 1000) : 0);

    return {
        timestamp,
        snapshotSlot: BigInt(api.snapshotSlot),
        snapshotTs: api.snapshotTs,
        shareSupply: BigInt(api.shareSupply),
        navPerShareE18: api.navPerShareE18,
        totalAumAssets: BigInt(api.totalAumAssets),
        pendingWithdrawalsAssets: BigInt(api.pendingWithdrawalsAssets),
        leverageX100: api.leverageX100,
        utilizationBps: api.utilizationBps,
        displayApyBps: api.displayApyBps,
        realizedReturnBps24h: api.realizedReturnBps24h,
        realizedReturnBps7d: api.realizedReturnBps7d,
        realizedReturnBps30d: api.realizedReturnBps30d,
        rewardsApyBps: api.rewardsApyBps,
        headlineApyBps: api.headlineApyBps
    };
}

export function parseEarnVaultFromApi(api: EarnVaultWithLatestSnapshotApi): EarnVaultRecord {
    return {
        ...api,
        acceptedAssetMints: api.acceptedAssetMints?.length ? api.acceptedAssetMints : [api.acceptedAssetMint],
        lastUpdatedSlot: api.lastUpdatedSlot ?? undefined,
        latestSnapshot: parseEarnVaultSnapshotFromApi(api.latestSnapshot),
        parsedAccountJson: parseParsedEarnAccountFromApi(api)
    };
}

export function parseEarnVaultListFromApi(api: EarnVaultWithLatestSnapshotApi[]): EarnVaultRecord[] {
    return api.map(parseEarnVaultFromApi);
}

export function parseEarnVaultSnapshotListFromApi(api: EarnVaultSnapshotApi[]): EarnVaultSnapshot[] {
    return api.map((s): EarnVaultSnapshot => {
        const parsed = parseEarnVaultSnapshotFromApi(s);
        // parseEarnVaultSnapshotFromApi only returns null for null/undefined inputs.
        return parsed as EarnVaultSnapshot;
    });
}
